/**
 * Subtle haptic feedback on intentional taps so the WKWebView surface
 * feels native rather than web. Fire-and-forget — never blocks UI.
 *
 * Style taxonomy mirrors UIKit's three families (see contract types.ts):
 *   - impact  : light | medium | heavy | soft | rigid
 *   - selection (subtle tick for value changes / nav switches)
 *   - notification : success | warning | error
 *
 * Stub mode + missing bridge are no-ops.
 *
 * Debounce: caps to one firing per MIN_INTERVAL_MS so a tap that triggers
 * both a click handler and downstream navigation doesn't double-fire.
 */
import type { TriggerHapticRequest } from "@contract/types";
import { bridge } from "../bridge/client";

export type HapticStyle = TriggerHapticRequest["style"];

const MIN_INTERVAL_MS = 50;
let lastFireAt = 0;

export function haptic(style: HapticStyle): void {
  const now =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  if (now - lastFireAt < MIN_INTERVAL_MS) return;
  lastFireAt = now;
  // Fire-and-forget — never await, never surface errors. Haptics are
  // a "nice to have" layer; their absence shouldn't break any flow.
  void bridge.triggerHaptic(style).catch(() => {});
}
